/**
 * Where the Scale Mediation portal lives, as seen from Case Manager.
 *
 * Case Manager and the portal are separate deployments on separate hosts, so
 * `window.location.origin` is never the right answer — CM's domain does not
 * serve /embed or the onboarding form, and a link built from it is dead.
 *
 * This logic existed in three places (OnboardingLinks.jsx, BookingLinkTab.jsx
 * and the Booking Link page), each carrying a comment telling the next person
 * to keep it in step with the others by hand. That is exactly how the UAT bug
 * below survived: it was fixed in the copies that had it and simply absent from
 * the one that did not. One exported function instead, so the next fix lands
 * everywhere at once.
 */

/**
 * Resolve the portal origin, without a trailing slash.
 *
 *   1. REACT_APP_SCALE_PORTAL_URL when explicitly set (env override).
 *   2. Otherwise derive it from the Case Manager host we are being served from.
 *
 * Step 2 is not a nicety — it is what keeps this working at all. Create React
 * App inlines REACT_APP_* at BUILD time, and the variable is set in local .env
 * but NOT in the deployed Vercel projects, so on UAT step 1 yields undefined.
 * Any caller relying on the env var alone silently produces an empty origin.
 *
 * The `uat` test must stay ABOVE the staging test and must not be folded into
 * it. A UAT host contains neither "staging" nor "localhost", so before this
 * case existed UAT fell through to the production branch and handed out
 * app.scalemediation.com links for cases that only exist on UAT — real clients
 * sent to a case that was not there.
 */
export function resolvePortalOrigin() {
  if (process.env.REACT_APP_SCALE_PORTAL_URL) {
    return process.env.REACT_APP_SCALE_PORTAL_URL.replace(/\/+$/, '');
  }
  const host = typeof window !== 'undefined' ? window.location.hostname : '';

  if (host.includes('uat')) return 'https://app-uat.scalemediation.com';

  const isStaging =
    host.includes('staging') ||
    host.includes('localhost') ||
    host.includes('127.0.0.1') ||
    host.includes('.onrender.com');
  return isStaging
    ? 'https://staging.scalemediation.com'
    : 'https://app.scalemediation.com';
}

/**
 * The public booking page for one mediator, or '' when it cannot be built.
 *
 * Keyed STRICTLY on the mediator's Supabase user_id. The portal's
 * /embed/:mediator route resolves the mediator by user_id (uuid), so falling
 * back to a numeric mediator_id would not fail loudly — it would mint a
 * well-formed URL that resolves to nothing. An empty string is the honest
 * answer; the caller decides what to say about it.
 */
export function bookingUrlForUserId(userId) {
  const origin = resolvePortalOrigin();
  return origin && userId ? `${origin}/embed/${userId}` : '';
}
